import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, RotateCcw, Sparkles } from 'lucide-react';
import { client, errorText } from '@/lib/api';
import type { plans } from '@/lib/client';
import { useDocumentsStore } from '@/stores/documentsStore';

/** How often a plan whose run is under way or stopped short looks for the request read again. */
const LOOK_MS = 5000;

const ACTIVE: ReadonlySet<plans.PlanStatus> = new Set(['running', 'failed', 'cancelled', 'revert_partial']);

/**
 * Back to planning (plans/replan.ts): once a step the API handed to the browser comes back to the person, ZAC reads
 * the request again for what is left on its own; the page says so and links the new plan. While a step waits or
 * failed, the person may send ZAC back to planning too.
 */
export function BackToPlanning({ plan }: { plan: plans.PlanView }) {
  const navigate = useNavigate();
  const docId = plan.document_id;
  const doc = useDocumentsStore((s) => (docId === undefined ? undefined : s.byId[docId]));
  const load = useDocumentsStore((s) => s.load);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const replanned = doc?.superseded_by ?? null;
  const watching = docId !== undefined && replanned === null && ACTIVE.has(plan.status);

  useEffect(() => {
    if (!watching) return;
    void load(docId);
    const timer = window.setInterval(() => { void load(docId); }, LOOK_MS);
    return () => window.clearInterval(timer);
  }, [watching, docId, load]);

  if (docId === undefined) return null;
  if (replanned !== null) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-100">
        <Sparkles className="h-4 w-4 shrink-0 text-sky-300" />
        <span className="flex-1">ZAC went back to planning for what's left of this run, told what's done and why the rest wasn't.</span>
        <Link to={`/changes/documents/${replanned}`} className="rounded-md bg-sky-500/20 px-2.5 py-1 text-xs font-medium hover:bg-sky-500/30">See the new plan</Link>
      </div>
    );
  }
  const stuck = plan.steps.some((step) => step.run?.status === 'waiting_human' || step.run?.status === 'failed');
  if (!stuck || !ACTIVE.has(plan.status)) return null;
  const replan = async () => {
    setBusy(true);
    setError(null);
    try {
      const { document_id } = await client.plans.replanPlan(plan.id);
      navigate(`/changes/documents/${document_id}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground">
      <span className="flex-1">A step is stuck. ZAC can go back to planning for what's left, a different way where this one failed.</span>
      <button onClick={() => void replan()} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-secondary disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Go back to planning
      </button>
      {error && <span className="w-full text-xs text-red-400">{error}</span>}
    </div>
  );
}
